import { supabase } from '../lib/supabase';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Analyze scenario to get dynamic form fields (no auth required)
export const analyzeScenario = async (scenario) => {
  const response = await axios.post(`${API_BASE_URL}/api/analyze-scenario`, 
    { scenario },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  return response.data;
};

// MAIN FUNCTION: Submit form data to backend API
export const submitGenerationJob = async (data) => {
  const formDataToSend = new FormData();
  const frontendOnlyFields = ['assets', 'dynamic_input'];

  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined || frontendOnlyFields.includes(key)) return;

    if ((key === 'availableTags' || key === 'fields') && Array.isArray(value) && value.length > 0) {
      formDataToSend.append(key, JSON.stringify(value));
      return;
    }

    if (Array.isArray(value) && value.length > 0) {
      if (value[0] instanceof File) {
        value.forEach((file, idx) => formDataToSend.append(`${key}${idx}`, file));
      } else {
        formDataToSend.append(key, JSON.stringify(value));
      }
      return;
    }

    if (value instanceof File) {
      formDataToSend.append(key, value);
      return;
    }

    if (typeof value === 'object') {
      formDataToSend.append(key, JSON.stringify(value));
      return;
    }

    if (typeof value === 'string' && value.trim()) {
      formDataToSend.append(key, value);
    }
  });

  // Handle assets separately - can be File objects, URL strings, or objects with url property
  if (data.assets?.length > 0) {
    const assetFiles = [];
    const assetUrls = [];
    
    data.assets.forEach((asset, idx) => {
      if (asset instanceof File) {
        assetFiles.push({ index: idx, file: asset });
        formDataToSend.append(`asset${idx}`, asset);
      } else if (typeof asset === 'string') {
        assetUrls.push(asset);
      } else if (typeof asset === 'object' && asset !== null && asset.url) {
        assetUrls.push(asset.url);
      }
    });
    
    if (assetUrls.length > 0) {
      formDataToSend.append('assetUrls', JSON.stringify(assetUrls));
    }
  }

  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  // API CALL: POST /api/generate
  const response = await axios.post(`${API_BASE_URL}/api/generate`, formDataToSend, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`,
    }
  });

  return {
    jobId: response.data.jobId,
    projectName: response.data.projectName,
  };
};

// Get job status from backend
export const getJobStatus = async (jobId) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  // API CALL: GET /api/status/:jobId
  const response = await axios.get(`${API_BASE_URL}/api/status/${jobId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  });

  return response.data;
};

// Get latest request history for the authenticated user
export const getLatestHistory = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  // API CALL: GET /api/history/latest
  const response = await axios.get(`${API_BASE_URL}/api/history/latest`, {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  });

  return response.data;
};

// Get request history by ID
export const getHistoryById = async (requestId) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  // API CALL: GET /api/history/:id
  const response = await axios.get(`${API_BASE_URL}/api/history/${requestId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  });

  return response.data;
};

// Get all request history for the authenticated user
export const getAllUserHistory = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  // API CALL: GET /api/history
  const response = await axios.get(`${API_BASE_URL}/api/history`, {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  });

  return response.data;
};

// Collections API functions

// Get all collections for the authenticated user
export const getAllCollectionsByUserId = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  const response = await axios.get(`${API_BASE_URL}/api/collections`, {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  });

  return response.data;
};

// Get collection by ID
export const getCollectionById = async (collectionId) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  const headers = {};
  // Only add authorization header if user is authenticated
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await axios.get(`${API_BASE_URL}/api/collections/${collectionId}`, {
    headers
  });

  return response.data;
};

// Create a new collection
export const addCollection = async (collectionData) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  const response = await axios.post(`${API_BASE_URL}/api/collections`, collectionData, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  });

  return response.data;
};

// Update collection by ID
export const updateCollectionById = async (collectionId, collectionData) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  const response = await axios.put(`${API_BASE_URL}/api/collections/${collectionId}`, collectionData, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  });

  return response.data;
};

// Delete collection by ID
export const deleteCollectionById = async (collectionId) => {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  const response = await axios.delete(`${API_BASE_URL}/api/collections/${collectionId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    }
  });

  return response.data;
};

// Get image by ID (public endpoint - works with or without authentication)
// If authenticated, returns full data including saved status
// If not authenticated, returns basic public image data
export const getImageById = async (imageId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const headers = {};
    // Only add authorization header if user is authenticated
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await axios.get(`${API_BASE_URL}/api/images/${imageId}`, {
      headers
    });

    return response.data;
  } catch (error) {
    // Handle errors gracefully for public access
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to get image');
    }
    throw error;
  }
};